// src/pages/ActivosForm.jsx
import React, { useEffect, useState } from 'react';
import {
  useForm,
  Controller,
  useFieldArray
} from 'react-hook-form';
import FormularioEnModal from '../components/FormularioEnModal';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { nanoid } from 'nanoid';
import { HardDrive, Smartphone, Monitor, Usb, Trash2, ArrowRight, CheckCircle, Save, Zap } from 'lucide-react';
import { Keyboard, Wifi, Plus, ArrowLeft, FileText } from '../components/icons';
import StepWizard from '../components/StepWizard';
import LocationSelector from '../components/LocationSelector';
import EmpresaSelector from '../components/EmpresaSelector';
import { normalizarEmpresa } from '../utils/empresaUtils';
import DropdownSelector from '../components/DropdownSelector';
import CPU_DATA_GEN from '../data/cpuData';
import { useNotificationContext } from '../contexts/NotificationContext';
import api from '../services/api';

const categorias = [
  { key: 'Equipos', prefix: 'EQUIPO', icon: HardDrive, color: 'from-blue-500 to-blue-600' },
  { key: 'Móviles', prefix: 'MOV', icon: Smartphone, color: 'from-green-500 to-green-600' },
  { key: 'Monitores', prefix: 'MON', icon: Monitor, color: 'from-purple-500 to-purple-600' },
  { key: 'Red', label: 'Redes y Comunicaciones', prefix: 'RED', icon: Wifi, color: 'from-cyan-500 to-cyan-600' },
];

const marcasEquipos = ['HP', 'Dell', 'Lenovo', 'Asus', 'Acer', 'Apple', 'Samsung', 'LG', 'Sony', 'Toshiba'];
const marcasMonitores = [
  'Dell', 'HP', 'LG', 'Samsung', 'AOC', 'ASUS', 'Acer', 'BenQ', 'ViewSonic', 'Philips',
  'Lenovo', 'MSI', 'Gigabyte', 'Sceptre', 'Xiaomi', 'Sony'
];
const rams = [4, 8, 16, 24, 32, 64, 128];
const discosCap = [128, 256, 480, 512, 1024, 2048];
const OS_OPTIONS = [
  'Windows 11',
  'Windows 10',
  'macOS Sequoia',
  'macOS Sonoma',
  'macOS Ventura',
  'macOS Monterey',
  'macOS Big Sur',
];

// Datos de CPUs encadenados (básico, fallback si CPU_DATA_GEN no tiene datos)
const CPU_DATA = {
  Intel: {
    'Core i3': ['i3-10100', 'i3-12100'],
    'Core i5': ['i5-10400', 'i5-12400'],
    'Core i7': ['i7-9700', 'i7-12700K'],
    'Core i9': ['i9-9900', 'i9-12900K'],
  },
  AMD: {
    'Ryzen 3': ['Ryzen 3 3100', 'Ryzen 3 4100'],
    'Ryzen 5': ['Ryzen 5 5600G', 'Ryzen 5 7600'],
    'Ryzen 7': ['Ryzen 7 5800X', 'Ryzen 7 7700X'],
    'Ryzen 9': ['Ryzen 9 5900X', 'Ryzen 9 7950X'],
  },
  Apple: {
    'M1': ['M1', 'M1 Pro', 'M1 Max'],
    'M2': ['M2', 'M2 Pro', 'M2 Max'],
  },
};

// Utilidades de orden
const sortSeries = (brand, seriesKeys) => {
  if (!Array.isArray(seriesKeys)) return [];
  if (brand === 'Intel') {
    const desired = ['Core i3', 'Core i5', 'Core i7', 'Core i9', 'Core Ultra', 'Other'];
    return [...seriesKeys].sort((a, b) => desired.indexOf(a) - desired.indexOf(b));
  }
  if (brand === 'AMD') {
    const desired = ['Ryzen 3', 'Ryzen 5', 'Ryzen 7', 'Ryzen 9'];
    return [...seriesKeys].sort((a, b) => desired.indexOf(a) - desired.indexOf(b));
  }
  if (brand === 'Apple') {
    return [...seriesKeys].sort((a, b) => {
      const na = Number(String(a).replace(/[^0-9]/g, '')) || 0;
      const nb = Number(String(b).replace(/[^0-9]/g, '')) || 0;
      return na - nb;
    });
  }
  return seriesKeys;
};

const sortGenerations = (brand, genKeys) => {
  if (!Array.isArray(genKeys)) return [];
  if (brand === 'Intel') {
    // "14th Gen (Raptor Lake Refresh - H/HX/U)" -> 14
    return [...genKeys].sort((a, b) => {
      const ga = parseInt(String(a).match(/^(\d+)/)?.[1] || '0', 10);
      const gb = parseInt(String(b).match(/^(\d+)/)?.[1] || '0', 10);
      return ga - gb;
    });
  }
  if (brand === 'AMD') {
    // "Ryzen 7000 (Zen 4)" -> 7000
    return [...genKeys].sort((a, b) => {
      const ga = parseInt(String(a).match(/Ryzen\s+(\d{4})/)?.[1] || '0', 10);
      const gb = parseInt(String(b).match(/Ryzen\s+(\d{4})/)?.[1] || '0', 10);
      return ga - gb;
    });
  }
  if (brand === 'Apple') return genKeys; // única categoría
  return genKeys;
};

const findProcessorDetails = (procesadorStr) => {
  if (!procesadorStr) return {};

  const normalize = (str) => str.toLowerCase().replace(/[^a-z0-9]/g, '');
  const target = normalize(procesadorStr);

  // 1. Buscar en CPU_DATA_GEN
  if (CPU_DATA_GEN) {
    for (const [marca, seriesObj] of Object.entries(CPU_DATA_GEN)) {
      // seriesObj keys son "Core i7", "Ryzen 7", etc. (Series)
      for (const [serie, gensObj] of Object.entries(seriesObj)) {
        // gensObj keys son "12th Gen...", "Ryzen 5000...", etc. (Generaciones)
        if (Array.isArray(gensObj)) {
          // Caso raro: Serie -> [Modelos] (sin generación intermedia)
          for (const modelo of gensObj) {
            if (target.includes(normalize(modelo))) {
              return { procMarca: marca, procSerie: serie, procGeneracion: '', procModelo: modelo };
            }
          }
        } else {
          // Caso normal: Serie -> Generación -> [Modelos]
          for (const [gen, modelos] of Object.entries(gensObj)) {
            for (const modelo of modelos) {
              if (target.includes(normalize(modelo))) {
                return { procMarca: marca, procSerie: serie, procGeneracion: gen, procModelo: modelo };
              }
            }
          }
        }
      }
    }
  }

  // 2. Buscar en CPU_DATA (fallback)
  for (const [marca, seriesObj] of Object.entries(CPU_DATA)) {
    for (const [serie, modelos] of Object.entries(seriesObj)) {
      for (const modelo of modelos) {
        if (target.includes(normalize(modelo))) {
          return { procMarca: marca, procSerie: serie, procGeneracion: '', procModelo: modelo };
        }
      }
    }
  }

  return {};
};

export default function ActivosForm({ edit = false, modal = false, onClose, onSaved, categoriaInicial, idProp }) {
  const { id: codigoRuta } = useParams();
  const codigo = idProp ?? codigoRuta;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const { notifyActivoCreated, alertSuccess, alertError } = useNotificationContext();

  // Obtener categoría preseleccionada de URL
  const categoriaPreseleccionada = categoriaInicial || searchParams.get('categoria') || 'Equipos';

  const {
    register,
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      categoria: categoriaPreseleccionada,
      codigoAuto: '',
      nombreEquipo: '',
      estado: 'Activo',
      ubicacion: '',
      tipoEquipo: '',
      // procesador encadenado
      procMarca: '',
      procSerie: '',
      procModelo: '',
      procesador: '',
      sistemaOperativo: '',
      // detalles
      serie: '',
      ram: '',
      marca: '',
      modelo: '',
      discos: [{ tipo: 'SSD', capacidad: '' }],
      pulgadas: '',
      imei: '',
      capacidadMovil: '',
      numeroCelular: '',
      cantidad: 1,
      empresa: '',
      valor: '',
      observaciones: '',
    },
  });

  const [recientes, setRecientes] = useState([]);
  const [plantillaAplicada, setPlantillaAplicada] = useState(null);

  const categoria = watch('categoria');
  const nombreEquipo = watch('nombreEquipo');
  const { fields: discos, append, remove } = useFieldArray({
    control,
    name: 'discos',
  });

  // load edit
  useEffect(() => {
    if (edit && codigo) {
      api.get(`/activos/${codigo}`)
        .then(({ data }) => {
          // Normalizar datos para el formulario
          const formData = { ...data };

          // Mapear código
          formData.codigoAuto = data.codigo;

          // Normalizar categoría
          const cat = data.categoria?.toLowerCase();
          if (['notebook', 'laptop', 'computador', 'desktop', 'servidor', 'workstation'].includes(cat)) {
            formData.categoria = 'Equipos';
          } else if (['pantalla', 'monitor'].includes(cat)) {
            formData.categoria = 'Monitores';
          } else if (['celular', 'smartphone', 'movil', 'móvil'].includes(cat)) {
            formData.categoria = 'Móviles';
          } else if (['teclado', 'mouse', 'auriculares', 'webcam'].includes(cat)) {
            formData.categoria = 'Periféricos';
          } else if (['cargador', 'cable', 'adaptador', 'docking'].includes(cat)) {
            formData.categoria = 'Accesorios';
          } else if (['router', 'switch', 'ap', 'firewall'].includes(cat)) {
            formData.categoria = 'Red';
          }

          // Parsear JSONs si vienen como string
          if (typeof data.discosJson === 'string') {
            try {
              formData.discos = JSON.parse(data.discosJson);
            } catch (e) {
              console.error('Error parsing discosJson:', e);
              formData.discos = [{ tipo: 'SSD', capacidad: '' }];
            }
          } else {
            // Si no hay discosJson, intentar obtener desde 'almacenamiento'
            let parsedFromString = false;

            if (data.almacenamiento) {
              // Buscar patrón "480 GB SSD" o similar
              const match = data.almacenamiento.match(/(\d+)\s*GB\s*(SSD|HDD)/i);
              if (match) {
                formData.discos = [{
                  capacidad: match[1],
                  tipo: match[2].toUpperCase()
                }];
                parsedFromString = true;
              }
            }

            if (!parsedFromString) {
              formData.discos = [{ tipo: 'SSD', capacidad: '' }];
            }
          }

          // Asegurar que la capacidad de los discos sea string para el dropdown
          if (formData.discos && Array.isArray(formData.discos)) {
            formData.discos = formData.discos.map(d => ({
              ...d,
              capacidad: d.capacidad ? String(d.capacidad) : ''
            }));
          }

          // Intentar parsear detalles del procesador si existe
          if (formData.procesador) {
            const procDetails = findProcessorDetails(formData.procesador);
            if (procDetails.procMarca) {
              formData.procMarca = procDetails.procMarca;
              formData.procSerie = procDetails.procSerie;
              formData.procGeneracion = procDetails.procGeneracion;
              formData.procModelo = procDetails.procModelo;
            }
          }

          reset(formData);
        })
        .catch(console.error);
    }
  }, [edit, codigo, reset]);

  // últimos activos de la categoría, para crear uno nuevo a partir de otro
  useEffect(() => {
    if (edit || !categoria) return;
    let cancelado = false;
    setPlantillaAplicada(null);
    api.get('/activos/recientes', { params: { categoria, limit: 6 } })
      .then(({ data }) => { if (!cancelado) setRecientes(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelado) setRecientes([]); });
    return () => { cancelado = true; };
  }, [edit, categoria]);

  // copia todo menos nombre/serie/IMEI/número, que se escriben a mano
  const aplicarPlantilla = r => {
    let discos = [{ tipo: 'SSD', capacidad: '' }];
    try {
      const j = r.discosJson ? JSON.parse(r.discosJson) : null;
      if (Array.isArray(j) && j.length) discos = j.map(d => ({ ...d, capacidad: d.capacidad ? String(d.capacidad) : '' }));
    } catch { /* usa el disco por defecto */ }
    const campos = {
      ubicacion: r.ubicacion || '',
      empresa: r.empresa || '',
      valor: r.valor != null ? String(Math.round(Number(r.valor))) : '',
      tipoEquipo: r.tipoEquipo || '',
      procesador: r.procesador || '',
      sistemaOperativo: r.sistemaOperativo || '',
      ram: r.ram || '',
      marca: r.marca || '',
      modelo: r.modelo || '',
      pulgadas: r.pulgadas || '',
      capacidadMovil: r.capacidad || '',
      observaciones: r.observaciones || '',
    };
    Object.entries(campos).forEach(([k, v]) => setValue(k, v, { shouldDirty: true }));
    setValue('discos', discos);
    if (r.procesador) {
      const pd = findProcessorDetails(r.procesador);
      if (pd.procMarca) {
        setValue('procMarca', pd.procMarca);
        setValue('procSerie', pd.procSerie);
        setValue('procGeneracion', pd.procGeneracion);
        setValue('procModelo', pd.procModelo);
      }
    }
    setValue('nombreEquipo', '');
    setValue('serie', '');
    setValue('imei', '');
    setValue('numeroCelular', '');
    setPlantillaAplicada(r.id);
  };

  // generar codigo Auto para no-Equipos
  useEffect(() => {
    if (categoria && categoria !== 'Equipos') {
      const pref = categorias.find(c => c.key === categoria)?.prefix || 'GEN';
      setValue('codigoAuto', `${pref}-${nanoid(6).toUpperCase()}`);
    }
  }, [categoria, setValue]);

  // para Equipos, el codigoAuto = nombreEquipo
  useEffect(() => {
    if (categoria === 'Equipos') {
      setValue('codigoAuto', nombreEquipo || '');
    }
  }, [nombreEquipo, categoria, setValue]);

  const onSubmit = async data => {
    if (step < 3) return setStep(s => s + 1);

    // build payload según categoría
    const base = {
      categoria: data.categoria,
      codigo: data.codigoAuto,
      estado: data.estado,
      ubicacion: data.ubicacion.trim(),
      empresa: data.empresa,
      valor: data.valor === '' || data.valor == null ? undefined : Number(data.valor),
      observaciones: data.observaciones,
    };

    let detalles = {};
    switch (data.categoria) {
      case 'Equipos':
        detalles = {
          nombreEquipo: data.nombreEquipo,
          tipoEquipo: data.tipoEquipo,
          procesador: data.procesador,
          sistemaOperativo: data.sistemaOperativo,
          serie: data.serie,
          ram: data.ram,
          marca: data.marca,
          modelo: data.modelo,
          discosJson: JSON.stringify(data.discos)
        };
        break;
      case 'Monitores':
        detalles = {
          serie: data.serie,
          pulgadas: data.pulgadas,
          marca: data.marca,
          modelo: data.modelo
        };
        break;
      case 'Móviles':
        detalles = {
          modelo: data.modelo,
          marca: data.marca,
          imei: data.imei,
          capacidad: data.capacidadMovil,
          numeroCelular: data.numeroCelular
        };
        break;
      default:
        detalles = { nombre: data.nombre, cantidad: data.cantidad };
    }

    const payload = { ...base, ...detalles };

    try {
      let response;
      if (edit) {
        response = await api.put(`/activos/${codigo}`, payload);
        alertSuccess(`Activo ${data.codigoAuto} actualizado correctamente`);
      } else {
        response = await api.post('/activos', payload);
        notifyActivoCreated(response.data);
        alertSuccess(`Activo ${data.codigoAuto} creado correctamente`);
      }
      if (modal) { onSaved?.(response?.data); onClose?.(); } else navigate('/activos');
    } catch (e) {
      console.error('Error al guardar activo:', e);
      const msg = e.response?.data?.message || e.message;
      alertError(`Error al ${edit ? 'actualizar' : 'crear'} activo: ${msg}`);
    }
  };

  // resumen final
  const data = watch();
  const resumen = (() => {
    const out = [
      ['Categoría', categorias.find(c => c.key === data.categoria)?.label || data.categoria],
      ['Código', data.codigoAuto],
      ['Estado', data.estado],
      ['Ubicación', data.ubicacion],
      ['Empresa', data.empresa],
      ['Valor', data.valor !== '' && data.valor != null ? `$${Number(data.valor).toLocaleString('es-CL')}` : '—']
    ];
    if (data.categoria === 'Equipos') {
      out.push(['Nombre de equipo', data.nombreEquipo]);
      out.push(['Tipo de equipo', data.tipoEquipo]);
      out.push(['Procesador', data.procesador]);
      if (data.sistemaOperativo) {
        out.push(['Sistema Operativo', data.sistemaOperativo]);
      }
      out.push(['N° Serie', data.serie]);
      out.push(['RAM', `${data.ram} GB`]);
      out.push(['Marca', data.marca]);
      out.push(['Modelo', data.modelo]);
      (data.discos || []).forEach((d, i) => out.push([`Almacenamiento ${i + 1}`, `${d.tipo} ${d.capacidad} GB`]));
    } else if (data.categoria === 'Monitores') {
      out.push(
        ['N° Serie', data.serie],
        ['Pulgadas', `${data.pulgadas}"`],
        ['Marca', data.marca],
        ['Modelo', data.modelo]
      );
    } else if (data.categoria === 'Móviles') {
      out.push(
        ['Modelo', data.modelo],
        ['Marca', data.marca],
        ['IMEI', data.imei],
        ['Capacidad', `${data.capacidadMovil} GB`]
      );
    } else {
      out.push(['Nombre', data.nombre], ['Cantidad', data.cantidad]);
    }

    if (data.observaciones) {
      out.push(['Observaciones', data.observaciones]);
    }

    return out;
  })();

  return (
    <FormularioEnModal modal={modal} onClose={onClose}>
        {/* Header */}
        {!modal && (<div className="mb-8 flex flex-col items-center">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2 text-center">
            {edit ? 'Editar Activo' : 'Crear Nuevo Activo'}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6 text-center">
            {edit ? 'Modifica la información del activo' : 'Completa la información para registrar un nuevo activo'}
          </p>
          <button
            onClick={() => navigate('/activos')}
            className="inline-flex items-center space-x-2 px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Volver</span>
          </button>
        </div>)}

        {/* Main Form Container */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <StepWizard current={step} />

          <form onSubmit={handleSubmit(onSubmit)} className="p-8">
            {step === 1 && (
              <div className="space-y-8">
                {/* Categoría */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                    <HardDrive className="w-5 h-5 mr-2 text-blue-600" />
                    Selecciona la categoría del activo
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {categorias.map(c => (
                      <label key={c.key} className="cursor-pointer group">
                        <input
                          type="radio"
                          {...register('categoria', { required: 'Obligatorio' })}
                          value={c.key}
                          className="sr-only"
                        />
                        <div className={`
                          h-24 px-3 flex flex-col items-center justify-center text-center border-2 rounded-lg transition-all duration-300 transform group-hover:scale-105
                          ${categoria === c.key
                            ? `border-blue-500 bg-gradient-to-br ${c.color} text-white shadow-lg`
                            : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-500'
                          }
                        `}>
                          <c.icon className={`w-6 h-6 mb-2 ${categoria === c.key ? 'text-white' : 'text-gray-600 dark:text-gray-400'}`} />
                          <span className="font-medium text-xs leading-tight">{c.label || c.key}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                  {errors.categoria && (
                    <p className="text-red-500 mt-2 flex items-center">
                      <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                      {errors.categoria.message}
                    </p>
                  )}
                </div>

                {/* Crear a partir de un activo reciente */}
                {!edit && recientes.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-2">
                      <Zap className="w-4 h-4 text-amber-500" /> Agregar rápido desde un activo reciente
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Copia los datos del activo elegido. El nombre y el número de serie los completas tú.</p>
                    <div className="flex gap-3 overflow-x-auto pb-2 snap-x">
                      {recientes.map(r => (
                        <button key={r.id} type="button" onClick={() => aplicarPlantilla(r)}
                          className={`snap-start shrink-0 w-48 text-left p-3 rounded-xl border transition ${plantillaAplicada === r.id ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30' : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-blue-400'}`}>
                          <div className="text-sm font-semibold text-gray-900 dark:text-white truncate">{[r.marca, r.modelo].filter(Boolean).join(' ') || r.codigo}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                            {[r.procesador, r.ram && `${r.ram} GB`, r.pulgadas && `${r.pulgadas}"`, r.capacidad].filter(Boolean).join(' · ') || r.tipoEquipo || 'Sin detalles'}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 truncate mt-1">{normalizarEmpresa(r.empresa) || 'Sin empresa'}{r.valor != null ? ` · $${Number(r.valor).toLocaleString('es-CL')}` : ''}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Información básica */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Información básica
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Nombre equipo solo en Equipos */}
                    {categoria === 'Equipos' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Nombre de equipo
                        </label>
                        <input
                          {...register('nombreEquipo', { required: 'Obligatorio' })}
                          className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                          placeholder="Ej: EQUIPO-XXX"
                        />
                        {errors.nombreEquipo && (
                          <p className="text-red-500 mt-1 text-sm">{errors.nombreEquipo.message}</p>
                        )}
                      </div>
                    )}

                    {/* Tipo de equipo solo en Equipos */}
                    {categoria === 'Equipos' && (
                      <div>
                        <Controller
                          name="tipoEquipo"
                          control={control}
                          rules={{ required: 'Obligatorio' }}
                          render={({ field }) => (
                            <DropdownSelector
                              allowOther
                              label="Tipo de equipo"
                              value={field.value}
                              onChange={(v) => { field.onChange(v); }}
                              options={['Desktop', 'Notebook', 'Servidor', 'Workstation']}
                              placeholder="Selecciona un tipo"
                              required={true}
                            />
                          )}
                        />
                        {errors.tipoEquipo && (
                          <p className="text-red-500 mt-1 text-sm">{errors.tipoEquipo.message}</p>
                        )}
                      </div>
                    )}

                    {/* Ubicación */}
                    <div>
                      <Controller
                        name="ubicacion"
                        control={control}
                        rules={{ required: 'Obligatorio' }}
                        render={({ field }) => (
                          <LocationSelector
                            value={field.value}
                            onChange={field.onChange}
                            required={true}
                          />
                        )}
                      />
                      {errors.ubicacion && (
                        <p className="text-red-500 mt-1 text-sm">{errors.ubicacion.message}</p>
                      )}
                    </div>

                    {/* Empresa */}
                    <div>
                      <Controller
                        name="empresa"
                        control={control}
                        rules={{ required: 'Obligatorio' }}
                        render={({ field }) => (
                          <EmpresaSelector
                            value={field.value}
                            onChange={field.onChange}
                            required={true}
                          />
                        )}
                      />
                      {errors.empresa && (
                        <p className="text-red-500 mt-1 text-sm">{errors.empresa.message}</p>
                      )}
                    </div>

                    {/* Valor */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Valor del activo (CLP)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        inputMode="numeric"
                        placeholder="Ej: 650000"
                        {...register('valor')}
                        className="w-full px-4 py-3 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white"
                      />
                    </div>

                    {/* Código */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Código del activo
                      </label>
                      <input
                        {...register('codigoAuto', {
                          required: 'El código es obligatorio',
                        })}
                        readOnly
                        className="w-full px-4 py-3 bg-gray-100 dark:bg-gray-600 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white font-mono text-sm"
                      />
                      {errors.codigoAuto && (
                        <p className="text-red-500 mt-1 text-sm">{errors.codigoAuto.message}</p>
                      )}
                    </div>

                    {/* Estado */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Estado del activo
                      </label>
                      <Controller
                        name="estado"
                        control={control}
                        render={({ field }) => (
                          <div className="flex flex-wrap gap-3 p-1 bg-gray-100/50 dark:bg-gray-800/50 rounded-2xl border border-gray-200 dark:border-gray-700">
                            {['Activo', 'Disponible', 'En Mantenimiento', 'Retirado'].map(v => (
                              <button
                                key={v}
                                type="button"
                                onClick={() => field.onChange(v)}
                                className={`
                                  flex items-center space-x-2 px-5 py-2.5 rounded-xl transition-all duration-300 font-semibold text-sm
                                  ${field.value === v
                                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-xl scale-105 ring-2 ring-blue-500/50'
                                    : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-md'
                                  }
                                `}>
                                {field.value === v && <CheckCircle className="w-4 h-4 text-white animate-scale" />}
                                <span>{v}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-8">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-blue-600" />
                  Especificaciones técnicas
                </h3>

                {/* Equipos */}
                {categoria === 'Equipos' && (
                  <div className="space-y-8">
                    {/* Marca y Modelo */}
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6">
                      <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Información del fabricante</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <Controller
                            name="marca"
                            control={control}
                            rules={{ required: 'Obligatorio' }}
                            render={({ field }) => (
                              <DropdownSelector
                                allowOther
                                label="Marca"
                                value={field.value}
                                onChange={field.onChange}
                                options={marcasEquipos}
                                placeholder="Selecciona una marca"
                                required={true}
                              />
                            )}
                          />
                          {errors.marca && (
                            <p className="text-red-500 mt-1 text-sm">{errors.marca.message}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Modelo</label>
                          <input
                            {...register('modelo', { required: 'Obligatorio' })}
                            className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                            placeholder="Ej: ThinkPad X1 Carbon"
                          />
                          {errors.modelo && (
                            <p className="text-red-500 mt-1 text-sm">{errors.modelo.message}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Procesador y Sistema Operativo */}
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6">
                      <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Procesador y sistema</h4>
                      <div className="space-y-6">
                        {/* Marca */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <Controller
                              name="procMarca"
                              control={control}
                              rules={{ required: 'Obligatorio' }}
                              render={({ field }) => (
                                <DropdownSelector
                                  allowOther
                                  label="Marca CPU"
                                  value={field.value}
                                  onChange={(v) => {
                                    field.onChange(v);
                                    setValue('procSerie', '');
                                    setValue('procGeneracion', '');
                                    setValue('procModelo', '');
                                    const combined = v ? `${v}` : '';
                                    setValue('procesador', combined);
                                  }}
                                  options={Object.keys(CPU_DATA_GEN && Object.keys(CPU_DATA_GEN).length ? CPU_DATA_GEN : CPU_DATA)}
                                  placeholder="Selecciona marca"
                                  required={true}
                                />
                              )}
                            />
                            {errors.procMarca && (
                              <p className="text-red-500 mt-1 text-sm">{errors.procMarca.message}</p>
                            )}
                          </div>
                          {/* Serie */}
                          <div>
                            <Controller
                              name="procSerie"
                              control={control}
                              rules={{ required: 'Obligatorio' }}
                              render={({ field }) => (
                                <DropdownSelector
                                  allowOther
                                  label="Línea / Serie"
                                  value={field.value}
                                  onChange={(v) => {
                                    field.onChange(v);
                                    setValue('procGeneracion', '');
                                    setValue('procModelo', '');
                                    const marca = watch('procMarca');
                                    const combined = v ? `${marca} ${v}` : marca || '';
                                    setValue('procesador', combined);
                                  }}
                                  options={(() => {
                                    const marca = watch('procMarca');
                                    const source = (CPU_DATA_GEN && CPU_DATA_GEN[marca]) || CPU_DATA[marca] || {};
                                    const keys = Object.keys(source);
                                    return sortSeries(marca, keys);
                                  })()}
                                  placeholder="Selecciona serie"
                                  required={true}
                                />
                              )}
                            />
                            {errors.procSerie && (
                              <p className="text-red-500 mt-1 text-sm">{errors.procSerie.message}</p>
                            )}
                          </div>
                          {/* Generación */}
                          <div>
                            <Controller
                              name="procGeneracion"
                              control={control}
                              rules={{ required: 'Obligatorio' }}
                              render={({ field }) => (
                                <DropdownSelector
                                  allowOther
                                  label="Generación"
                                  value={field.value}
                                  onChange={(v) => {
                                    field.onChange(v);
                                    setValue('procModelo', '');
                                    const marca = watch('procMarca');
                                    const serie = watch('procSerie');
                                    const combined = [marca, serie, v].filter(Boolean).join(' ');
                                    setValue('procesador', combined);
                                  }}
                                  options={(() => {
                                    const marca = watch('procMarca');
                                    const serie = watch('procSerie');
                                    const srcSeries = (CPU_DATA_GEN && CPU_DATA_GEN[marca]) || CPU_DATA[marca] || {};
                                    const srcGen = srcSeries[serie];
                                    if (!srcGen) return [];
                                    const keys = Array.isArray(srcGen) ? [] : Object.keys(srcGen);
                                    return sortGenerations(marca, keys);
                                  })()}
                                  placeholder="Selecciona generación"
                                  required={true}
                                />
                              )}
                            />
                            {errors.procGeneracion && (
                              <p className="text-red-500 mt-1 text-sm">{errors.procGeneracion.message}</p>
                            )}
                          </div>
                          {/* Modelo */}
                          <div>
                            <Controller
                              name="procModelo"
                              control={control}
                              rules={{ required: 'Obligatorio' }}
                              render={({ field }) => (
                                <DropdownSelector
                                  allowOther
                                  label="Modelo"
                                  value={field.value}
                                  onChange={(v) => {
                                    field.onChange(v);
                                    const marca = watch('procMarca');
                                    const serie = watch('procSerie');
                                    const generacion = watch('procGeneracion');
                                    const combined = [marca, serie, generacion, v].filter(Boolean).join(' ');
                                    setValue('procesador', combined);
                                  }}
                                  options={(() => {
                                    const marca = watch('procMarca');
                                    const serie = watch('procSerie');
                                    const generacion = watch('procGeneracion');
                                    const srcSeries = (CPU_DATA_GEN && CPU_DATA_GEN[marca]) || CPU_DATA[marca];
                                    if (!srcSeries || !serie) return [];
                                    const srcGen = srcSeries[serie];
                                    if (Array.isArray(srcGen)) return srcGen; // fallback sin generación
                                    return generacion && srcGen[generacion] ? srcGen[generacion] : [];
                                  })()}
                                  placeholder="Selecciona modelo"
                                  required={true}
                                />
                              )}
                            />
                            {errors.procModelo && (
                              <p className="text-red-500 mt-1 text-sm">{errors.procModelo.message}</p>
                            )}
                          </div>
                        </div>
                        <div>
                          <Controller
                            name="sistemaOperativo"
                            control={control}
                            rules={{ required: false }}
                            render={({ field }) => (
                              <DropdownSelector
                                allowOther
                                label="Sistema Operativo"
                                value={field.value}
                                onChange={field.onChange}
                                options={OS_OPTIONS}
                                placeholder="Selecciona SO"
                              />
                            )}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Serie y RAM */}
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6">
                      <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Especificaciones técnicas</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">N° Serie</label>
                          <input
                            {...register('serie', { required: 'Obligatorio' })}
                            className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                            placeholder="Número de serie del equipo"
                          />
                          {errors.serie && (
                            <p className="text-red-500 mt-1 text-sm">{errors.serie.message}</p>
                          )}
                        </div>
                        <div>
                          <Controller
                            name="ram"
                            control={control}
                            rules={{ required: 'Obligatorio' }}
                            render={({ field }) => (
                              <DropdownSelector
                                allowOther
                                label="RAM"
                                value={field.value}
                                onChange={field.onChange}
                                options={rams.map(r => String(r))}
                                placeholder="Selecciona la RAM"
                                required={true}
                              />
                            )}
                          />
                          {errors.ram && (
                            <p className="text-red-500 mt-1 text-sm">{errors.ram.message}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Almacenamiento */}
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-md font-semibold text-gray-900 dark:text-white">Almacenamiento</h4>
                        <button
                          type="button"
                          onClick={() => append({ tipo: 'SSD', capacidad: '' })}
                          className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                        >
                          <Plus className="w-4 h-4" />
                          <span>Añadir</span>
                        </button>
                      </div>
                      <div className="space-y-4">
                        {discos.map((d, i) => (
                          <div key={d.id} className="flex items-center space-x-4 p-4 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                            <Controller
                              name={`discos.${i}.capacidad`}
                              control={control}
                              render={({ field }) => (
                                <DropdownSelector
                                  allowOther
                                  value={field.value}
                                  onChange={field.onChange}
                                  options={discosCap.map(c => String(c))}
                                  placeholder="Capacidad (GB)"
                                />
                              )}
                            />
                            <Controller
                              name={`discos.${i}.tipo`}
                              control={control}
                              render={({ field }) => (
                                <DropdownSelector
                                  allowOther
                                  value={field.value}
                                  onChange={field.onChange}
                                  options={['SSD', 'HDD']}
                                  placeholder="Tipo"
                                />
                              )}
                            />
                            {discos.length > 1 && (
                              <button
                                type="button"
                                onClick={() => remove(i)}
                                className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors duration-200"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Monitores */}
                {categoria === 'Monitores' && (
                  <div className="space-y-8">
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6">
                      <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Información del monitor</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <Controller
                            name="marca"
                            control={control}
                            rules={{ required: 'Obligatorio' }}
                            render={({ field }) => (
                              <DropdownSelector
                                allowOther
                                label="Marca"
                                value={field.value}
                                onChange={field.onChange}
                                options={marcasMonitores}
                                placeholder="Selecciona una marca"
                                required={true}
                              />
                            )}
                          />
                          {errors.marca && (
                            <p className="text-red-500 mt-1 text-sm">{errors.marca.message}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Modelo</label>
                          <input
                            {...register('modelo', { required: 'Obligatorio' })}
                            className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                            placeholder="Ej: UltraSharp U2720Q"
                          />
                          {errors.modelo && (
                            <p className="text-red-500 mt-1 text-sm">{errors.modelo.message}</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">N° Serie</label>
                          <input
                            {...register('serie', { required: 'Obligatorio' })}
                            className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                            placeholder="Número de serie del monitor"
                          />
                          {errors.serie && (
                            <p className="text-red-500 mt-1 text-sm">{errors.serie.message}</p>
                          )}
                        </div>
                        <div>
                          <Controller
                            name="pulgadas"
                            control={control}
                            rules={{ required: 'Obligatorio' }}
                            render={({ field }) => (
                              <DropdownSelector
                                allowOther
                                label="Pulgadas"
                                value={field.value}
                                onChange={field.onChange}
                                options={['19', '21.5', '22', '23.8', '24', '27', '28', '32', '34']}
                                placeholder="Selecciona tamaño"
                                required={true}
                              />
                            )}
                          />
                          {errors.pulgadas && (
                            <p className="text-red-500 mt-1 text-sm">{errors.pulgadas.message}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Móviles */}
                {categoria === 'Móviles' && (
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6">
                    <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Información del dispositivo móvil</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Controller
                          name="marca"
                          control={control}
                          rules={{ required: 'Obligatorio' }}
                          render={({ field }) => (
                            <DropdownSelector
                              allowOther
                              label="Marca"
                              value={field.value}
                              onChange={field.onChange}
                              options={['Apple', 'Samsung', 'Xiaomi', 'Honor', 'Motorola', 'Huawei', 'Nokia', 'Google', 'OnePlus', 'OPPO', 'Realme', 'Lenovo', 'ZTE', 'LG', 'Sony']}
                              placeholder="Selecciona una marca"
                              required={true}
                            />
                          )}
                        />
                        {errors.marca && (
                          <p className="text-red-500 mt-1 text-sm">{errors.marca.message}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Modelo</label>
                        <input
                          {...register('modelo', { required: 'Obligatorio' })}
                          placeholder="Ej: iPhone 14 Pro"
                          className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        />
                        {errors.modelo && (
                          <p className="text-red-500 mt-1 text-sm">{errors.modelo.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">IMEI</label>
                        <input
                          {...register('imei', { required: 'Obligatorio' })}
                          placeholder="Número IMEI del dispositivo"
                          className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        />
                        {errors.imei && (
                          <p className="text-red-500 mt-1 text-sm">{errors.imei.message}</p>
                        )}
                      </div>
                      <div>
                        <Controller
                          name="capacidadMovil"
                          control={control}
                          rules={{ required: 'Obligatorio' }}
                          render={({ field }) => (
                            <DropdownSelector
                              allowOther
                              label="Capacidad (GB)"
                              value={field.value}
                              onChange={field.onChange}
                              options={['32', '64', '128', '256', '512', '1024']}
                              placeholder="Selecciona capacidad"
                              required={true}
                            />
                          )}
                        />
                        {errors.capacidadMovil && (
                          <p className="text-red-500 mt-1 text-sm">{errors.capacidadMovil.message}</p>
                        )}
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Número Celular</label>
                        <input
                          {...register('numeroCelular', { required: false })}
                          placeholder="+56 9 1234 5678"
                          className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                        />
                        {errors.numeroCelular && (
                          <p className="text-red-500 mt-1 text-sm">{errors.numeroCelular.message}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Periféricos, Accesorios, Red */}
                {['Periféricos', 'Accesorios', 'Red'].includes(categoria) && (
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6">
                    <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Información del {categoria === 'Red' ? 'equipo de red' : categoria.toLowerCase().slice(0, -1)}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Nombre</label>
                        <input
                          {...register('nombre', { required: 'Obligatorio' })}
                          className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                          placeholder={categoria === 'Red' ? 'Nombre del equipo de red' : `Nombre del ${categoria.toLowerCase().slice(0, -1)}`}
                        />
                        {errors.nombre && (
                          <p className="text-red-500 mt-1 text-sm">{errors.nombre.message}</p>
                        )}
                      </div>
                      <input type="hidden" value="1" {...register('cantidad')} />
                    </div>
                  </div>
                )}

                {/* Observaciones Generales (Aplica para todas las categorías) */}
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6">
                  <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Observaciones</h4>
                  <div className="w-full">
                    <textarea
                      {...register('observaciones')}
                      rows={4}
                      className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                      placeholder="Agrega notas o información adicional sobre este activo..."
                    ></textarea>
                  </div>
                </div>

              </div>
            )}

            {/* Paso 3: Resumen */}
            {step === 3 && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
                  Resumen del activo
                </h3>
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {resumen.map(([label, val]) => (
                      <div key={label} className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-600 last:border-b-0">
                        <span className="font-medium text-gray-700 dark:text-gray-300">{label}:</span>
                        <span className="text-gray-900 dark:text-white">{val || 'No especificado'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Navegación */}
            <div className="flex justify-between items-center pt-8 border-t border-gray-200 dark:border-gray-700">
              {step > 1 && (
                <button
                  type="button"
                  onClick={() => setStep(s => s - 1)}
                  className="flex items-center space-x-2 px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-all duration-200"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span>Atrás</span>
                </button>
              )}
              <button
                type="submit"
                disabled={isSubmitting}
                className={`
                  flex items-center space-x-2 px-8 py-3 rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105
                  ${step < 3
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700'
                    : 'bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:from-green-700 hover:to-emerald-700'
                  }
                  disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
                `}
              >
                {step < 3 ? (
                  <>
                    <span>Siguiente</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                ) : isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Guardando…</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Confirmar y guardar</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
    </FormularioEnModal>
  );
}
